/**
 * Schema-driven CRUD component
 * Automatically generates CRUD interface from entity configuration
 */

import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Plus, Edit, Trash2 } from 'lucide-react';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/lib/api-client';
import { toast } from 'react-hot-toast';
import {
	SCHEMA_CONFIGS,
	getFieldLabel,
	getFieldRenderer,
	getInputType,
	getFieldOptions,
} from '@/lib/schema-crud';

interface SchemaCRUDProps {
	entity: string; // e.g., "users", "roles", "blockchains"
	title: string;
	description?: string;
	apiEndpoint: string; // e.g., "v1/users", "v1/admin/roles"
}

export function SchemaCRUD({ entity, title, description, apiEndpoint }: SchemaCRUDProps) {
	const [data, setData] = useState<any[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [editingItem, setEditingItem] = useState<any | null>(null);
	const [formData, setFormData] = useState<Record<string, any>>({});
	const [loading, setLoading] = useState(false);

	const config = SCHEMA_CONFIGS[entity];

	if (!config) {
		return <div>Entity configuration not found for: {entity}</div>;
	}

	// Load data from API
	useEffect(() => {
		loadData();
	}, []);

	const loadData = async () => {
		setLoading(true);
		try {
			const response = await apiClient.list(apiEndpoint, { limit: 100, offset: 0 });

			if (response.success && response.data) {
				setData(response.data);
			} else {
				toast.error(response.error || 'Erro ao carregar dados');
			}
		} catch (error) {
			console.error('Error loading data:', error);
			toast.error('Erro ao carregar dados');
		} finally {
			setLoading(false);
		}
	};

	// Filter data based on search
	const filteredData = data.filter((item) => {
		if (!searchTerm) return true;
		return config.searchFields.some((field) => {
			const value = item[field];
			return value && String(value).toLowerCase().includes(searchTerm.toLowerCase());
		});
	});

	const initializeFormData = (item?: any) => {
		const newFormData: Record<string, any> = {};
		config.formFields.forEach((field) => {
			newFormData[field] = item?.[field] || '';
		});
		return newFormData;
	};

	const handleCreate = () => {
		setEditingItem(null);
		setFormData(initializeFormData());
		setIsDialogOpen(true);
	};

	const handleEdit = (item: any) => {
		setEditingItem(item);
		setFormData(initializeFormData(item));
		setIsDialogOpen(true);
	};

	const handleDelete = async (item: any) => {
		if (!window.confirm('Tem certeza que deseja excluir este item?')) return;

		setLoading(true);
		try {
			const response = await apiClient.remove(apiEndpoint, item.id);

			if (response.success) {
				toast.success('Item excluído com sucesso');
				await loadData();
			} else {
				toast.error(response.error || 'Erro ao excluir');
			}
		} catch (error) {
			console.error('Error deleting:', error);
			toast.error('Erro ao excluir');
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);

		try {
			let response;
			if (editingItem) {
				response = await apiClient.update(apiEndpoint, editingItem.id, formData);
			} else {
				response = await apiClient.create(apiEndpoint, formData);
			}

			if (response.success) {
				toast.success(editingItem ? 'Item atualizado com sucesso' : 'Item criado com sucesso');
				setIsDialogOpen(false);
				setFormData({});
				setEditingItem(null);
				await loadData();
			} else {
				toast.error(response.error || 'Erro ao salvar');
			}
		} catch (error) {
			console.error('Error saving:', error);
			toast.error('Erro ao salvar');
		} finally {
			setLoading(false);
		}
	};

	const renderField = (fieldName: string) => {
		const value = formData[fieldName] || '';
		const inputType = getInputType({ name: fieldName, type: 'string' });
		const options = getFieldOptions(fieldName);

		switch (inputType) {
			case 'textarea':
				return (
					<textarea
						id={fieldName}
						value={value}
						onChange={(e) => setFormData({ ...formData, [fieldName]: e.target.value })}
						className="flex min-h-[80px] w-full rounded-md border border-input bg-[var(--background)] px-3 py-2 text-sm"
					/>
				);

			case 'select':
				return (
					<select
						id={fieldName}
						value={value}
						onChange={(e) => setFormData({ ...formData, [fieldName]: e.target.value })}
						className="flex h-10 w-full rounded-md border border-input bg-[var(--background)] px-3 py-2 text-sm"
					>
						<option value="">Selecione...</option>
						{options.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
				);

			case 'checkbox':
				return (
					<div className="flex items-center space-x-2">
						<input
							type="checkbox"
							id={fieldName}
							checked={value}
							onChange={(e) => setFormData({ ...formData, [fieldName]: e.target.checked })}
							className="h-4 w-4 rounded border-input"
						/>
					</div>
				);

			default:
				return (
					<Input
						type={inputType}
						id={fieldName}
						value={value}
						onChange={(e) => setFormData({ ...formData, [fieldName]: e.target.value })}
					/>
				);
		}
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div>
				<h1 className="text-3xl font-bold text-foreground">{title}</h1>
				{description && <p className="text-muted-foreground mt-2">{description}</p>}
			</div>

			{/* Search and Create */}
			<Card>
				<CardContent className="p-4">
					<div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
						<div className="relative flex-1 max-w-sm">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
							<Input
								placeholder={`Buscar ${title.toLowerCase()}...`}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-9"
							/>
						</div>
						<Button onClick={handleCreate}>
							<Plus className="h-4 w-4 mr-2" />
							Adicionar
						</Button>
					</div>
				</CardContent>
			</Card>

			{/* Table */}
			<Card>
				<CardContent className="p-0">
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead className="border-b">
								<tr className="bg-muted/50">
									{config.displayFields.map((field) => (
										<th
											key={field}
											className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
										>
											{getFieldLabel(field)}
										</th>
									))}
									<th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
										Ações
									</th>
								</tr>
							</thead>
							<tbody className="divide-y">
								{loading ? (
									<tr>
										<td
											colSpan={config.displayFields.length + 1}
											className="px-6 py-12 text-center text-muted-foreground"
										>
											Carregando...
										</td>
									</tr>
								) : filteredData.length === 0 ? (
									<tr>
										<td
											colSpan={config.displayFields.length + 1}
											className="px-6 py-12 text-center text-muted-foreground"
										>
											{searchTerm ? 'Nenhum resultado encontrado' : 'Nenhum item encontrado'}
										</td>
									</tr>
								) : (
									filteredData.map((item) => (
										<tr key={item.id} className="hover:bg-muted/30">
											{config.displayFields.map((field) => (
												<td key={field} className="px-6 py-4 whitespace-nowrap text-sm">
													{getFieldRenderer(field, 'string')(item)}
												</td>
											))}
											<td className="px-6 py-4 whitespace-nowrap text-right text-sm">
												<div className="flex items-center justify-end gap-2">
													<Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
														<Edit className="h-4 w-4" />
													</Button>
													<Button variant="ghost" size="icon" onClick={() => handleDelete(item)}>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</CardContent>
			</Card>

			{/* Create/Edit Dialog */}
			<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
				<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
					<form onSubmit={handleSubmit}>
						<DialogHeader>
							<DialogTitle>
								{editingItem ? 'Editar' : 'Adicionar'} {title}
							</DialogTitle>
							<DialogDescription>
								{editingItem
									? 'Edite os campos abaixo para atualizar o registro.'
									: 'Preencha os campos abaixo para criar um novo registro.'}
							</DialogDescription>
						</DialogHeader>

						<div className="grid gap-4 py-4">
							{config.formFields.map((field) => (
								<div key={field} className="grid gap-2">
									<Label htmlFor={field}>{getFieldLabel(field)}</Label>
									{renderField(field)}
								</div>
							))}
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => setIsDialogOpen(false)}
								disabled={loading}
							>
								Cancelar
							</Button>
							<Button type="submit" disabled={loading}>
								{loading ? 'Salvando...' : editingItem ? 'Salvar' : 'Criar'}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}
